/**
 * BetaFeedbackContext — the single source of truth for the in-app
 * feedback engagement system. Every feedback CTA (dashboard card,
 * floating button, banner, smart prompt, support page) goes through
 * this provider so analytics, modal state, and dismissals stay
 * consistent.
 *
 * Surfaces opening this modal:
 *   • dashboard_card    — DashboardFeedbackCard
 *   • floating_button   — FloatingFeedbackButton
 *   • beta_banner       — BetaTestingBanner
 *   • smart_prompt      — SmartFeedbackPrompt (post star-rating)
 *   • support_page      — SupportHelp "Beta Feedback" CTA
 *   • rating_prompt     — SmartFeedbackPrompt itself (rating impressions)
 *
 * Every surface fires `impression` on mount and `click` on tap via
 * trackEvent(); the modal fires `submit_open` and `submit_success`.
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BetaFeedbackContext = createContext(null);

export const BetaFeedbackProvider = ({ children }) => {
  const [modalState, setModalState] = useState({ open: false, source: null, prefill: null });

  // Fire-and-forget — analytics writes must never block UX.
  const trackEvent = useCallback((event_type, surface, extra = {}) => {
    try {
      axios.post(
        `${API}/feedback/track-event`,
        { event_type, surface, ...extra },
        { withCredentials: true },
      ).catch(() => { /* silent */ });
    } catch { /* silent */ }
  }, []);

  // Each surface uses this to open the modal AND log a 'click' + 'submit_open'.
  // `prefill` lets the Smart Feedback Prompt seed the rating from
  // the star tap into the modal.
  const openFeedbackModal = useCallback((source, prefill = null) => {
    trackEvent('click', source);
    trackEvent('submit_open', source);
    setModalState({ open: true, source, prefill });
  }, [trackEvent]);

  const closeFeedbackModal = useCallback(() => {
    setModalState({ open: false, source: null, prefill: null });
  }, []);

  // Called once after a successful POST /api/feedback/submit so consumers
  // can fire confetti, close the prompt, etc. Surface stays attached so
  // analytics can attribute the conversion.
  const recordSubmitSuccess = useCallback((source, rating = null) => {
    trackEvent('submit_success', source, rating ? { rating } : {});
  }, [trackEvent]);

  // Section visit tracker — Dashboard / module pages call this on mount
  // so the >=3 unique-sections-visited trigger works. Server uses
  // $addToSet, so duplicate calls are cheap & idempotent.
  const trackSectionVisit = useCallback((section) => {
    if (!section) return;
    try {
      axios.post(
        `${API}/feedback/section-visit`,
        { section },
        { withCredentials: true },
      ).catch(() => { /* silent */ });
    } catch { /* silent */ }
  }, []);

  const value = useMemo(() => ({
    modalState,
    openFeedbackModal,
    closeFeedbackModal,
    recordSubmitSuccess,
    trackEvent,
    trackSectionVisit,
  }), [modalState, openFeedbackModal, closeFeedbackModal, recordSubmitSuccess, trackEvent, trackSectionVisit]);

  return (
    <BetaFeedbackContext.Provider value={value}>
      {children}
    </BetaFeedbackContext.Provider>
  );
};

export const useBetaFeedback = () => {
  const ctx = useContext(BetaFeedbackContext);
  if (!ctx) {
    // Soft fallback — components like FloatingFeedbackButton may be
    // rendered outside the provider during error states; return no-ops.
    return {
      modalState: { open: false, source: null, prefill: null },
      openFeedbackModal: () => {},
      closeFeedbackModal: () => {},
      recordSubmitSuccess: () => {},
      trackEvent: () => {},
      trackSectionVisit: () => {},
    };
  }
  return ctx;
};
